import React, { FC } from 'react';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { ExtraLink, SocialMedia } from './types';
import Button from 'components/commercetools-ui/atoms/button';

interface Props {
  logo?: ImageProps;
  logoDescription?: string;
  socialMedia?: SocialMedia[];
  extraLinks?: ExtraLink[];
  showCookieButton?: boolean;
  cookieButtonLabel?: string;
  copyrightTitle?: string;
  copyrightDescription?: string;
}

const FooterBottom: FC<Props> = ({
  logo,
  logoDescription,
  socialMedia,
  extraLinks,
  showCookieButton,
  cookieButtonLabel = 'Cookie settings',
  copyrightTitle,
  copyrightDescription,
}) => {
  const currentYear = new Date().getFullYear();
  const formattedCopyrightTitle = copyrightTitle?.replace(/{year}/g, currentYear.toString());

  return (
    <>
      {/* Desktop */}
      <div className="container hidden items-center justify-between pt-36 lg:flex xl:pt-48">
        {logo && (
          <div className="w-192">
            <div className="flex flex-col gap-8">
              <Image {...logo} alt="logo" />
              <div className="flex px-7">
                <Typography className="body-text-x-small text-center text-brand-tertiary">{logoDescription}</Typography>
              </div>
            </div>
          </div>
        )}
        <ul className="flex flex-row gap-20 self-center">
          {socialMedia?.map((item, i) => (
            <li key={i} className="w-22">
              <Link link={item.reference}>
                <Image {...item.logo} className="mb-20" />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Extra Links + Copyrights (Desktop) */}
      <div className="container hidden w-full items-center justify-between py-32 lg:flex xl:py-32">
        <div className="flex flex-col gap-10 text-brand-tertiary">
          <Typography className="body-text">{formattedCopyrightTitle}</Typography>
          <Typography className="body-text-x-small">{copyrightDescription}</Typography>
        </div>

        {extraLinks && extraLinks.length > 0 && (
          <div className="body-text-small flex items-center gap-8 text-brand-tertiary">
            {extraLinks.map((link, index) => (
              <React.Fragment key={index}>
                <Link link={link.reference} className="hover:underline">
                  {link.name}
                </Link>
                {index < extraLinks.length - 1 && <span className="text-neutral-500">|</span>}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {showCookieButton && (
        <div className="container hidden w-full items-center justify-between pb-32 lg:flex xl:pb-32">
          <Button className={'px-24 py-12'} variant={'primary'} id="cookie-button-desktop">
            {cookieButtonLabel}
          </Button>
        </div>
      )}

      {/* Mobile*/}
      <div className="container flex flex-col items-center py-32 lg:hidden">
        <div className="flex flex-col items-center justify-center gap-32">
          {logo && (
            <div className="mb-16 w-192">
              <div className="flex flex-col gap-12">
                <Image {...logo} alt="logo" />
                <div className="flex px-5">
                  <Typography className="body-text-x-small text-center text-brand-tertiary">
                    {logoDescription}
                  </Typography>
                </div>
              </div>
            </div>
          )}
          <ul className="flex flex-row gap-20 self-center">
            {socialMedia?.map((item, i) => (
              <li key={i} className="w-24">
                <Link link={item.reference}>
                  <Image {...item.logo} />
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex flex-col items-center gap-24">
            {extraLinks && extraLinks.length > 0 && (
              <div className="body-text-small flex items-center gap-8 text-center text-brand-tertiary">
                {extraLinks.map((link, index) => (
                  <React.Fragment key={index}>
                    <Link link={link.reference} className="hover:underline">
                      {link.name}
                    </Link>
                    {index < extraLinks.length - 1 && <span className="text-neutral-500">|</span>}
                  </React.Fragment>
                ))}
              </div>
            )}
            <div className="flex flex-col gap-10 text-center">
              <div className="flex flex-col gap-10 text-brand-tertiary">
                <Typography className="body-text leading-6">{formattedCopyrightTitle}</Typography>
                <Typography className="body-text-x-small">{copyrightDescription}</Typography>
              </div>
            </div>
          </div>
          <div className="flex items-center">
            {showCookieButton && (
              <div className="w-full items-center justify-between px-24">
                <Button className={'px-24 py-12'} variant={'primary'} id="cookie-button-mobile">
                  {cookieButtonLabel}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FooterBottom;
